// keyOwner — WHO owns a key press, in one place (FRONTEND-PATTERNS §Row interaction): a page's single-key
// shortcuts (a list's walk — ↑ ↓ · j k · Enter; a ticket peek's R) listen on the window and stand down while
// the person types or a picker owns the keys, and while a MODAL sits on top. An overlay held in the link is never
// "on top" of its OWN keys: on a phone its sheet IS a React Aria drawer, so the sheet marks its face with the
// overlay it holds (`OVERLAY_SHEET` = `peek` · `file` · `job`) and the dialog framing that face does not count for
// that overlay's keys — any other dialog does, another overlay's sheet included (the photo viewer opened over the
// peek is a drawer too on a phone; a confirm; a drawer form).

/** The attribute an overlay's sheet sets on its face — its value the overlay's URL param; the dialog around it frames
 *  that overlay, never a modal over that overlay's own keys. */
export const OVERLAY_SHEET = 'data-overlay-sheet';

/** The person types, or a picker owns the keys. */
export function isEditable(target: EventTarget | null): boolean {
  // A keydown targets an element (the focused one, else the body) — never the window; a
  // non-element target (a synthetic dispatch) is nothing a person types into.
  const el = target instanceof HTMLElement ? target : null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable || el.closest('[role="listbox"], [role="combobox"], [role="menu"]') !== null;
}

/** A MODAL owns the keys of `overlay` (the URL param whose keys ask — `peek`): the file surface, a drawer, a confirm
 *  dialog. Our overlays ride React Aria, which marks its dialogs `data-rac` and hides the rest of the page
 *  (`aria-hidden`) instead of stamping `aria-modal`; the wide peek is a hand-rolled NON-modal `<div role="dialog">`
 *  and never counts; a React Aria drawer framing `overlay`'s own sheet never counts either — one framing another
 *  overlay's sheet does. */
export function modalOwnsKeys(overlay: string): boolean {
  for (const dialog of document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="dialog"][data-rac]')) {
    const sheet = dialog.querySelector(`[${OVERLAY_SHEET}="${overlay}"]`);
    if (!sheet || sheet.closest('[role="dialog"]') !== dialog) return true;
  }
  return false;
}
